import React, { useEffect, useState } from 'react';

import FavoriteIcon from '@mui/icons-material/Favorite';
import HeartBrokenIcon from '@mui/icons-material/HeartBroken';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';

import { Province, ProvinceOp } from '../../../@types/KingdomResponse';
import { ProvinceLite } from '../../../@types/State';
import { autosizeOptions } from '../../../Constants';
import { useIntelState } from '../../../context/IntelState';
import { effects } from '../../../Effects';
import RestUtilities from '../../../services/RestUtilities';
import OpsToolBar from '../components/OpsToolBar';
import { StripedDataGrid } from '../components/StripedDataGrid';
import { getSvgItemWithTooltip } from '../helpers/colHelpers';

const columns: GridColDef[] = [
  { field: 'id', headerName: 'ID', hideable: true },
  {
    field: 'utoDate',
    headerName: 'Date',
  },
  {
    field: 'result',
    headerName: 'Result',
    width: 40,
    renderCell: (params: GridRenderCellParams) =>
      params.row.result ? (
        <FavoriteIcon color='success' />
      ) : (
        <HeartBrokenIcon color='error' />
      ),
  },
  {
    field: 'provinceName',
    headerName: 'Province',
  },
  {
    field: 'targetName',
    headerName: 'Target',
  },
  {
    field: 'opIcon',
    headerName: 'Op',
    valueGetter: (value, row) => {
      const opType = row.opType.replaceAll('_', ' ');
      const effect =
        effects[opType.replaceAll(' ', '').replaceAll("'", '').toUpperCase()];
      return effect?.shortName;
    },
    renderCell: (params: GridRenderCellParams) => {
      const opType = params.row.opType.replaceAll('_', ' ');
      const effect =
        effects[opType.replaceAll(' ', '').replaceAll("'", '').toUpperCase()];
      return effect ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {getSvgItemWithTooltip(effect.svg, effect.displayName)}
          <span style={{ marginLeft: '3px' }}>{effect.shortName}</span>
        </div>
      ) : (
        opType
      );
    },
  },
  {
    field: 'duration',
    headerName: 'Duration',
    valueGetter: (value, row) => (row.duration ? `${row.duration} ticks` : ''),
  },
  {
    field: 'gain',
    headerName: 'Gain',
    align: 'right',
    valueGetter: (value, row) => (row.gain ? `${row.gain}` : ''),
  },
  {
    field: 'damage',
    headerName: 'Damage',
    align: 'right',
    valueGetter: (value, row) => (row.damage ? `${row.damage}` : ''),
  },
];

const KingdomOpsGrid = () => {
  const { intelState } = useIntelState();
  const [data, setData] = useState<ProvinceOp[] | undefined>(undefined);
  const [rows, setRows] = useState<ProvinceOp[] | undefined>(undefined);
  const [opTypes, setOpTypes] = useState<string[] | undefined>(undefined);
  const [opType, setOpType] = useState<string>('All Ops');
  const [loading, setLoading] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 25,
    page: 0,
  });
  const [province, setProvince] = useState<ProvinceLite | undefined>(undefined);

  const provinces: ProvinceLite[] | undefined =
    intelState.ownData?.provinces.map((x: Province) => {
      return {
        id: x.id,
        name: x.name,
        race: x.race,
        slot: `[${x.id}]`,
      } as ProvinceLite;
    });

  const handleProvinceChange = (event: any) => {
    const prov: ProvinceLite | undefined = provinces?.find(
      (x: ProvinceLite) => x.id == event.target.value
    );
    if (prov) {
      setProvince(prov);
    } else {
      setProvince(undefined);
    }
  };

  const handleOpTypeChange = (event: any) => {
    setOpType(event.target.value);
  };

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      const response = await RestUtilities.get(
        `Kingdom/v1/KingdomOps?server=${intelState.server}`
      );
      if (response.ok) {
        const resp: ProvinceOp[] = response.content;
        setData(resp);
      } else {
        setData(undefined);
      }
      setLoading(false);
    };
    getData();
  }, [intelState.server]);

  useEffect(() => {
    // Apply DropDown Filters
    let filteredData = data;
    if (province) {
      filteredData = data?.filter(
        (x: ProvinceOp) => x.provinceName === province.name
      );
    }
    if (opType !== 'All Ops') {
      filteredData = filteredData?.filter((o: ProvinceOp) => o.name === opType);
    }
    setRows(filteredData);
  }, [province, data, opType]);

  useEffect(() => {
    const unique = data ? Array.from(new Set(data.map((op) => op.name))) : [];
    if (unique.length) {
      setOpTypes(unique.sort());
    }
  }, [data]);

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {rows && (
        <StripedDataGrid
          columns={columns}
          rows={rows}
          density='compact'
          loading={loading}
          autosizeOptions={autosizeOptions}
          autosizeOnMount={true}
          paginationModel={paginationModel}
          columnVisibilityModel={{ id: false }}
          onPaginationModelChange={setPaginationModel}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          showToolbar
          slots={{
            toolbar: OpsToolBar,
            columnMenuUnsortIcon: null,
          }}
          slotProps={{
            toolbar: {
              provinces,
              province,
              handleProvinceChange,
              opTypes,
              opType,
              handleOpTypeChange,
            },
          }}
        />
      )}
    </div>
  );
};

export default KingdomOpsGrid;
